/** @jsx React.createElement */
/** @jsxFrag React.Fragment */
import {Button, Typography} from 'onboarding-components';
import * as React from 'react';
import map from 'lodash/map';
import clsx from 'clsx';

import {ScrollableElement, scrollToStep} from 'common/onboarding/immersive-onboarding/components/scroll-manager';
import {TileButton as TileButtonBase} from 'common/onboarding/components/tile-button';
import {SelectReact} from 'common/components/select';
import {SelectTheme} from 'common/components/select/constants';
import {SvgIconReact} from 'common/components/svg-icon';
import {IMMERSIVE_ASSETS} from 'common/onboarding/immersive-onboarding/assets/assets';
import {INDUSTRY_EVENTS, IndustryKey, PROPERTIES} from 'common/onboarding/immersive-onboarding/constants';
import {updateEventSelection, updateStepsCompleted} from 'common/report/queries/app-api/immersive-onboarding';
import {TooltipReact} from 'common/components/tooltip';
import {Placement} from 'common/components/tooltip/types';
import {Banner, BannerType} from 'common/onboarding/immersive-onboarding/components/banner';
import {Content, Header} from 'common/onboarding/immersive-onboarding/components';
import {TextInput} from 'common/onboarding/immersive-onboarding/components/text-input';
import {AppContext, BreakdownType, SetupGuideContext, Step} from 'common/onboarding/immersive-onboarding/types';
import {addOrgUsers} from 'common/report/queries/app-api/legacy/orgs-settings-client';
import {isValidEmailAddress} from 'common/util/string';
import {promptIntroModal} from 'common/widgets/intro';
import {Permission} from 'common/report/constants';
import {track, TrackingEvent, TrackingProperty} from 'common/onboarding/immersive-onboarding/tracking';

import {EventChartPreviewCarousel, PropertyBreakdownPreviewCarousel} from './chart-preview';
import styles from './events.module.styl';

type HeaderProps = {title: string; subTitle: string};

enum SubStep {
  EventConfirmation = 1,
  Segmentation = 2,
  AccessToCode = 3,
  TeamInvite = 4,
}

type SectionProps = {
  subStepId: SubStep;
  onNext?: () => void;
  onSkip?: () => void;
  events?: string[];
  blur?: boolean;
};

const SubStepContext = React.createContext<{
  subStep: SubStep;
  setSubStep: React.Dispatch<React.SetStateAction<SubStep>>;
}>(null);

const NUM_INVITE_TEAMMATE = 3;

const scrollToSubStep = (subStep: SubStep) => {
  scrollToStep(SubStep[subStep]);
};

// Slight variation of TileButton from iron/common/onboarding/components/tile-button/index.tsx
const TileButton = (props) => {
  const {className, ...rest} = props;
  return <TileButtonBase className={clsx(props.className, styles.tileButtonShadow)} {...rest} />;
};

const SectionHeader = ({title, subTitle}: HeaderProps) => {
  return (
    <div>
      <Typography variant='headingXs' style={{margin: 0}}>
        {title}
      </Typography>
      <Typography variant='bodySm'>{subTitle}</Typography>
    </div>
  );
};

const Section = (props: React.PropsWithChildren<HeaderProps & SectionProps>) => {
  const {children, onNext, onSkip, subStepId, ...headerProps} = props;
  const {subStep: currentSubStep} = React.useContext(SubStepContext);

  return (
    <ScrollableElement name={SubStep[subStepId]} active={subStepId <= currentSubStep}>
      <div className={clsx(styles.section, {[styles.blur]: props.blur})}>
        <div className={styles.substep}>{subStepId}</div>
        <div className={styles.sectionContent}>
          <SectionHeader {...headerProps} />
          {children}
          <div className={styles.footerButtons}>
            <Button onClick={onNext} size='small' disabled={onNext === null}>
              Next
            </Button>
            {onSkip && (
              <Button onClick={onSkip} variant={`inline`} includeArrow={false} size='small'>
                Skip
              </Button>
            )}
          </div>
        </div>
      </div>
    </ScrollableElement>
  );
};

const EventBanner = () => {
  const {setupGuideInfo} = React.useContext(SetupGuideContext);
  const [visible, setVisible] = React.useState(true);
  return (
    <Banner
      title={`New to event based analytics?`}
      description={`Learn how it all works in 2 minutes`}
      actionName={`Learn more`}
      action={() => {
        promptIntroModal({'first-name': setupGuideInfo.author});
        setVisible(false);
        track(TrackingEvent.EventsLearnMoreClick);
      }}
      type={BannerType.Marketing}
      isVisible={visible}
    />
  );
};

const EventSelector = ({selected, onIndustryKeyChange, events, updateEvents}) => {
  const EVENT_INPUT_MAX = 6;

  const dropdownOptions = Object.keys(INDUSTRY_EVENTS).map((el) => ({value: el, label: INDUSTRY_EVENTS[el].name}));

  const deleteEvent = (eventIndex) => {
    updateEvents((events) => events.filter((_, index) => index !== eventIndex));
  };

  return (
    <div className={styles.sectionTile}>
      <SelectReact
        sections={[{items: dropdownOptions}]}
        disableSearch
        onChange={(ev) => {
          onIndustryKeyChange({industryKey: ev.detail.item.value as IndustryKey});
        }}
        theme={SelectTheme.Tertiary}
        alwaysShowCaret
        menuWidth={150}
      >
        <div slot={`trigger`}>
          <Button variant='inline' includeArrow={false} size='small' className={styles.buttonPadding}>
            {INDUSTRY_EVENTS[selected].name}
            <SvgIconReact icon='caret-down' style={{minHeight: 17, height: 17}} />
          </Button>
        </div>
      </SelectReact>
      <div className={styles.eventContainer}>
        {events.map((event, eventIndex) => (
          <div key={`${INDUSTRY_EVENTS[selected].name}-${event}`} className={styles.eventEditorRow}>
            <TextInput
              validator={(val) => val.trim().length > 0}
              defaultValue={event}
              onBlur={(value) => {
                updateEvents((events) => events.map((event, index) => (index === eventIndex ? value : event)));
              }}
            />
            <div
              className={clsx(styles.deleteIconWrapper, {[styles.disabled]: events.length === 1 && eventIndex === 0})}
              onClick={() => events.length > 1 && deleteEvent(eventIndex)}
            >
              <SvgIconReact className={styles.deleteIcon} icon={`trashcan`} />
              {events.length === 1 && eventIndex === 0 && (
                <TooltipReact placement={Placement.Top} text={`You must choose at least one event`} />
              )}
            </div>
          </div>
        ))}
        {events.length < EVENT_INPUT_MAX && (
          <TextInput
            key={Math.random()} // Hack to trick React rendering...
            placeholder={`Add Event`}
            onBlur={(value) => {
              if (value.trim().length > 0) {
                updateEvents((events) => [...events, value.trim()]);
              }
            }}
          />
        )}
        {/* -1 due to existing input box (5 events + 1 event input) */}
        {events.length >= EVENT_INPUT_MAX - 1 && (
          <Typography variant='captionSm' className={styles.eventLimitText}>
            We suggest tracking 6 or fewer events to start
          </Typography>
        )}
      </div>
    </div>
  );
};

const ConfirmEventsSection = () => {
  const {setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);
  const {setSubStep} = React.useContext(SubStepContext);

  const [selected, updateSelected] = React.useState<IndustryKey>(() =>
    setupGuideInfo ? setupGuideInfo.industry : `b2b`,
  );

  const [events, updateEvents] = React.useState(INDUSTRY_EVENTS[selected].events);

  React.useEffect(() => {
    if (setupGuideInfo !== null) {
      updateSelected(setupGuideInfo.industry);
      updateEvents(
        setupGuideInfo.events.length > 0 ? setupGuideInfo.events : INDUSTRY_EVENTS[setupGuideInfo.industry].events,
      );
    }
  }, [setupGuideInfo]);

  const onIndustryKeyChange = ({industryKey}: {industryKey: IndustryKey}) => {
    updateSelected(industryKey);
    if (setupGuideInfo.events.length === 0 || setupGuideInfo.industry !== industryKey) {
      updateEvents(INDUSTRY_EVENTS[industryKey].events);
    } else updateEvents(setupGuideInfo.events);
  };

  return (
    <Section
      subStepId={SubStep.EventConfirmation}
      title={`Plan your first events`}
      subTitle={`We recommend tracking fewer events at first. This list is a good starting point for your industry:`}
      onNext={() => {
        updateEventSelection(setupGuideInfo.project_id, events, selected).then((result) => {
          setSetupGuideInfo(result);
        });

        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Event confirmation`});
        setSubStep(SubStep.Segmentation);
      }}
    >
      <div className={styles.breakdownContent}>
        <div className={styles.selector}>
          <EventSelector
            selected={selected}
            events={events}
            onIndustryKeyChange={onIndustryKeyChange}
            updateEvents={updateEvents}
          />
        </div>
        <div className={styles.preview}>
          <EventChartPreviewCarousel events={events} />
        </div>
      </div>
    </Section>
  );
};

const BreakdownPreviewSection = () => {
  const {setupGuideInfo} = React.useContext(SetupGuideContext);
  const {setSubStep} = React.useContext(SubStepContext);

  const [selectedProperty, setSelectedProperty] = React.useState<BreakdownType>(`device_type`);

  return (
    <Section
      subStepId={SubStep.Segmentation}
      title='Properties help you segment your data'
      subTitle='Most mixpanel integration methods include default event properties, such as “Country”. You can use these properties to uncover trends. Try the example below:'
      onNext={() => {
        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Property breakdown`});
        setSubStep(SubStep.AccessToCode);
      }}
    >
      <div className={styles.breakdownContent}>
        <div className={styles.selector}>
          {map(PROPERTIES, (value, key: BreakdownType) => {
            return (
              <TileButton
                style={{width: `100%`}}
                key={key}
                onClick={() => setSelectedProperty(key)}
                selected={selectedProperty === key}
              >
                {value}
              </TileButton>
            );
          })}
          <span>
            These are sample default properties.{` `}
            <a
              href={`https://docs.mixpanel.com/docs/tracking/how-tos/events-and-properties`}
              target={`_blank`}
              onClick={() => {
                track(TrackingEvent.LearnMoreClick);
              }}
            >{`Learn more.`}</a>
          </span>
        </div>
        <div className={styles.preview}>
          <PropertyBreakdownPreviewCarousel events={setupGuideInfo?.events ?? []} property={selectedProperty} />
        </div>
      </div>
    </Section>
  );
};

const CodeAccessSection = ({updateAccessToCode}) => {
  const {setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);
  const {setSubStep} = React.useContext(SubStepContext);
  const [accessToCodeSelection, setAccessToCodeSelection] = React.useState(null);

  return (
    <Section
      subStepId={SubStep.AccessToCode}
      title={`You're ready to send events to Mixpanel. Do you typically add code to your codebase?`}
      subTitle={`You can always change this selection later`}
      onNext={
        accessToCodeSelection === null
          ? null
          : () => {
              track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Code access`});
              updateAccessToCode(accessToCodeSelection);
              if (accessToCodeSelection) {
                const updatedStepsCompleted = [...setupGuideInfo.steps_completed, Step.Events];
                updateStepsCompleted(setupGuideInfo.project_id, updatedStepsCompleted);
                setSetupGuideInfo({...setupGuideInfo, steps_completed: updatedStepsCompleted});
                scrollToStep(Step.Implement);
              } else {
                setSubStep(SubStep.TeamInvite);
              }
            }
      }
      onSkip={() => {
        scrollToStep(Step.Implement);
      }}
    >
      <div className={styles.accessCodeStepContainer}>
        <TileButton
          className={styles.accessCodeCta}
          selected={accessToCodeSelection}
          onClick={() => setAccessToCodeSelection(true)}
        >
          Yes, I add code snippets to my product
        </TileButton>
        <TileButton
          className={styles.accessCodeCta}
          selected={accessToCodeSelection === false}
          onClick={() => setAccessToCodeSelection(false)}
        >
          No, I need to invite a team member
        </TileButton>
      </div>
    </Section>
  );
};

const InviteSection = ({hasAccessToCode}) => {
  const {setupGuideInfo, setSetupGuideInfo} = React.useContext(SetupGuideContext);
  const [inviteToEmails, setInviteToEmails] = React.useState([]);
  const [inviteConfirmation, setInviteConfirmation] = React.useState([...Array(NUM_INVITE_TEAMMATE)].map(() => false));

  const {currentUser, currentProject} = React.useContext(AppContext);
  const {organizationId: currentOrgId, id: projectId} = currentProject.get().value ?? {};
  const canInviteUsers = currentUser.get().value?.organizations[currentOrgId]?.permissions.includes(Permission.AddUser);

  const completeEventSection = () => {
    const updatedStepsCompleted = [...setupGuideInfo.steps_completed, Step.Events];
    updateStepsCompleted(setupGuideInfo.project_id, updatedStepsCompleted);
    setSetupGuideInfo({...setupGuideInfo, steps_completed: updatedStepsCompleted});
  };

  return (
    <Section
      subStepId={SubStep.TeamInvite}
      title={`Invite a developer to help you set up Mixpanel — you have unlimited seats`}
      subTitle={`We recommend tracking fewer events at first. This list is a good starting point for your industry:`}
      onSkip={() => {
        completeEventSection();
        scrollToStep(Step.Implement);
      }}
      onNext={() => {
        track(TrackingEvent.SubStepCompleted, {[TrackingProperty.SubStepName]: `Invite developers`});

        if (inviteToEmails.length > 0) {
          addOrgUsers(
            currentOrgId,
            inviteToEmails.map((email) => ({
              email,
              name: ``,
              projects: [{id: projectId, role: `admin`}],
            })),
            {
              events: setupGuideInfo?.events.length > 0 ? setupGuideInfo.events : INDUSTRY_EVENTS[`b2b`].events,
              editor: setupGuideInfo.author,
            },
          ).then((result) => {
            const updatedConfirmationArray = [...inviteConfirmation];
            result.forEach(({invited, email}) => {
              const emailIndex = inviteToEmails.indexOf(email);
              if (invited) {
                updatedConfirmationArray[emailIndex] = true;
              }
            });
            setInviteConfirmation(updatedConfirmationArray);
            track(TrackingEvent.InviteDevelopers, {[TrackingProperty.Count]: inviteToEmails.length});
          });
        }
        completeEventSection();
        scrollToStep(Step.Implement);
      }}
      blur={hasAccessToCode !== false}
    >
      {!canInviteUsers && (
        <Typography variant='bodySm' className={styles.disableText} weight='bold'>
          You don’t have the permission to send invites. Ask an Owner/Admin to change your role.
        </Typography>
      )}
      <div className={clsx(styles.teammate, !canInviteUsers && styles.blur)}>
        {[...Array(NUM_INVITE_TEAMMATE)].map((_, index) => (
          <div className={styles.emailInputContainer} key={index}>
            <TextInput
              placeholder={`Email address`}
              validator={(val) => isValidEmailAddress(val)}
              parentHook={(newEmail) => {
                const newEmailList = [...inviteToEmails];
                newEmailList[index] = newEmail;
                setInviteToEmails(newEmailList);
              }}
            />
            <div className={styles.inviteConfirmation}>
              <Typography variant='captionSm'>{inviteConfirmation[index] && `Invite Sent`}</Typography>
            </div>
          </div>
        ))}
      </div>
    </Section>
  );
};

const Overview = () => {
  return (
    <div className={styles.overview}>
      <Typography variant='headingXs' style={{margin: 0}}>
        Overview
      </Typography>
      <ul>
        <li>
          <Typography variant='bodySm'>
            When you track the right events, its easy to learn about your product and customers
          </Typography>
        </li>
        <li>
          <Typography variant='bodySm'>
            This step by step-by-step guide will help you select the right events to track first
          </Typography>
        </li>
        <li>
          <Typography variant='bodySm'>
            Already know the events you want to track?{` `}
            <a
              onClick={() => {
                scrollToStep(Step.Implement);
                track(TrackingEvent.SkipStep, {[TrackingProperty.CurrentStep]: Step.Events});
              }}
            >
              Skip this step
            </a>
          </Typography>
        </li>
      </ul>
    </div>
  );
};

export const Events = () => {
  const [subStep, setSubStep] = React.useState<SubStep>(SubStep.EventConfirmation);
  const [hasAccessToCode, updateAccessToCode] = React.useState(null);
  const {completed} = React.useContext(SetupGuideContext);

  React.useEffect(() => {
    // Only scroll via effect if it's not the first subStep
    if (subStep && subStep !== 1) scrollToSubStep(subStep);
  }, [subStep]);

  return (
    <SubStepContext.Provider value={{subStep, setSubStep}}>
      <Header
        title={`Personalize your setup`}
        step={`step 2`}
        imgSrc={IMMERSIVE_ASSETS.toggles}
        duration={3}
        completed={completed[Step.Events]}
      />
      <Content>
        <EventBanner />
        <Overview />
        <ConfirmEventsSection />
        <BreakdownPreviewSection />
        <CodeAccessSection updateAccessToCode={updateAccessToCode} />
        <InviteSection hasAccessToCode={hasAccessToCode} />
      </Content>
    </SubStepContext.Provider>
  );
};
